/**
 * Shoplift JS test: sell the hair duplicate product so the test group can be priced separately.
 * Test 01a13ceb-f15c-7ca5-a800-d801c6639286
 * Control 01a13ceb-f15e-7eab-a629-988e4535d8dc, $10 off + GWP 01a13ceb-f160-7bf3-9881-b4a301dd6d25
 * Preview: ?slVariant=<group id> (set by Shoplift's preview switcher)
 */
;(() => {
  const TEST_ID = '01a13ceb-f15c-7ca5-a800-d801c6639286'
  const CONTROL_ID = '01a13ceb-f15e-7eab-a629-988e4535d8dc'
  const HYPOTHESIS_ID = '01a13ceb-f160-7bf3-9881-b4a301dd6d25'
  const SESSION_KEY = 'shoplift_hair_gwp_test_variant_v2'
  const RELOAD_KEY = `${SESSION_KEY}_reloaded_for`
  const TIMEOUT_MS = 3000
  const LIVE_HANDLES = ['os-01-hair', 'os-01-hair-peptide-scalp-serum-testing-drew']
  const CARTFUL = {
    control: {
      quizId: '019dffc2-2e5e-7bbf-994f-b285f1a3f7cb',
      flowId: '019dffc5-368c-7660-8758-158f76bde79c',
    },
    test: {
      quizId: '01a0b5ac-b44d-7119-a4b7-15378e73fa7e',
      flowId: '01a0b5ac-b4d9-7119-a4b7-d141dd8c3730',
    },
  }

  let assignmentPromise
  let testProduct

  // One sellable variant per supply from the "Hair test purchase product" theme setting,
  // preferring Without dermaroller when the product has a dermaroller option.
  function getTestProduct() {
    if (testProduct !== undefined) return testProduct
    testProduct = null
    const jsonEl = document.getElementById('hair-test-product-data')
    if (!jsonEl?.textContent) return testProduct

    let data
    try {
      data = JSON.parse(jsonEl.textContent)
    } catch (error) {
      console.warn('[hair-gwp-test] Failed to parse test product data', error)
      return testProduct
    }

    const sellable = (data.variants || []).filter((variant) => variant.selling_plan_id)
    const supplies = [...new Set(sellable.map((variant) => variant.option1))]
    const variants = supplies.map((supply) => {
      const options = sellable.filter((variant) => variant.option1 === supply)
      const variant =
        options.find((option) => !option.option2 || option.option2.toLowerCase().includes('without')) || options[0]
      return {
        id: variant.id,
        title: variant.option1,
        option1: variant.option1,
        price: variant.selling_plan_price ?? variant.price,
        compareAtPrice: variant.compare_at_price || 0,
        sellingPlanId: variant.selling_plan_id,
        sellingPlanGroupId: variant.selling_plan_group_id,
      }
    })
    if (variants.length === 0) return testProduct

    testProduct = {
      id: data.id,
      handle: data.handle,
      title: data.title,
      options: (data.options || []).slice(0, 1),
      variants,
    }
    return testProduct
  }

  function getCachedAssignment() {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY)
      if (cached === 'test' || cached === 'control') return cached
    } catch {
      /* ignore */
    }
    return null
  }

  function setCachedAssignment(value) {
    try {
      sessionStorage.setItem(SESSION_KEY, value)
    } catch {
      /* ignore */
    }
  }

  function waitForShoplift(timeout = TIMEOUT_MS) {
    return new Promise((resolve) => {
      if (window.shoplift && typeof window.shoplift.isHypothesisActive === 'function') {
        resolve(true)
        return
      }

      let elapsed = 0
      const interval = 50
      const timer = setInterval(() => {
        elapsed += interval
        if (window.shoplift && typeof window.shoplift.isHypothesisActive === 'function') {
          clearInterval(timer)
          resolve(true)
        } else if (elapsed >= timeout) {
          clearInterval(timer)
          resolve(false)
        }
      }, interval)
    })
  }

  // The visitor's group only when Shoplift is unambiguous: they have a record for this test and
  // exactly one group reports active. Ended tests report neither, so they return null.
  async function confirmedShopliftAssignment() {
    if (!(await waitForShoplift())) return null
    try {
      const record = window.shoplift.getVisitorData?.()?.visitorTests?.find((entry) => entry.testId === TEST_ID)
      if (!record) return null
      const [isTest, isControl] = await Promise.all([
        window.shoplift.isHypothesisActive(HYPOTHESIS_ID),
        window.shoplift.isHypothesisActive(CONTROL_ID),
      ])
      if (isTest === !isControl) return isTest ? 'test' : 'control'
    } catch (error) {
      console.warn('[hair-gwp-test] Failed to confirm Shoplift assignment', error)
    }
    return null
  }

  // A cached group from an earlier page can go stale (e.g. Shoplift's preview switcher).
  // Reload once when Shoplift confirms a different group.
  async function reconcileCachedAssignment(cached) {
    const confirmed = await confirmedShopliftAssignment()
    if (!confirmed || confirmed === cached) return
    setCachedAssignment(confirmed)
    try {
      if (sessionStorage.getItem(RELOAD_KEY) === confirmed) return
      sessionStorage.setItem(RELOAD_KEY, confirmed)
    } catch {
      return
    }
    window.location.reload()
  }

  async function resolveAssignment() {
    const params = new URLSearchParams(window.location.search)
    const slVariant = params.get('slVariant')
    if (slVariant === HYPOTHESIS_ID) {
      setCachedAssignment('test')
      return 'test'
    }
    if (slVariant === CONTROL_ID || slVariant === 'control') {
      setCachedAssignment('control')
      return 'control'
    }

    const cached = getCachedAssignment()
    if (cached) {
      reconcileCachedAssignment(cached)
      return cached
    }

    const available = await waitForShoplift()
    if (!available || !window.shoplift) {
      setCachedAssignment('control')
      return 'control'
    }

    try {
      const isTest = await window.shoplift.isHypothesisActive(HYPOTHESIS_ID)
      const assignment = isTest ? 'test' : 'control'
      setCachedAssignment(assignment)
      return assignment
    } catch (error) {
      console.warn('[hair-gwp-test] Failed to read Shoplift assignment', error)
      setCachedAssignment('control')
      return 'control'
    }
  }

  function isActive() {
    if (!assignmentPromise) assignmentPromise = resolveAssignment()
    return assignmentPromise.then((value) => value === 'test')
  }

  function productHandleFrom(container) {
    return (
      container?.dataset?.productHandle || container?.querySelector('[data-product-form]')?.dataset?.productHandle || ''
    )
  }

  function isLiveHairHandle(handle) {
    return LIVE_HANDLES.includes(handle)
  }

  function pathProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/)
    return match ? decodeURIComponent(match[1]) : ''
  }

  function supplyMonths(value) {
    return String(value || '').match(/\d+/)?.[0] || ''
  }

  function matchVariant(value) {
    const variants = getTestProduct().variants
    const months = supplyMonths(value)
    return variants.find((variant) => months && supplyMonths(variant.option1) === months) || variants[0]
  }

  function formatMoney(cents) {
    const value = Number(cents || 0) / 100
    if (Number.isNaN(value)) return ''
    return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`
  }

  function buildPurchaseProduct(liveProduct) {
    const { variants: testVariants, ...purchase } = getTestProduct()
    const liveVariants = liveProduct?.variants || []
    const variants = testVariants.map((variant) => {
      const supply = variant.option1.toLowerCase()
      const liveMatch =
        liveVariants.find((liveVariant) => {
          const liveSupply = String(liveVariant.option1 || liveVariant.title.split('/')[0])
            .trim()
            .toLowerCase()
          return liveSupply === supply
        }) || null

      return {
        id: variant.id,
        title: variant.title,
        option1: variant.option1,
        option2: null,
        options: [variant.option1],
        price: variant.price,
        compare_at_price: variant.compareAtPrice,
        available: true,
        requires_selling_plan: false,
        featured_media: liveMatch?.featured_media,
        featured_image: liveMatch?.featured_image,
        selling_plan_allocations: [
          {
            price: variant.price,
            compare_at_price: variant.price,
            selling_plan_id: variant.sellingPlanId,
            selling_plan_group_id: variant.sellingPlanGroupId,
          },
        ],
      }
    })

    return {
      ...purchase,
      media: liveProduct?.media,
      variants,
    }
  }

  function injectPurchaseJson(container, liveProduct) {
    let script = container.querySelector('[data-purchase-product-json]')
    if (!script) {
      script = document.createElement('script')
      script.type = 'application/json'
      script.setAttribute('data-purchase-product-json', '')
      container.appendChild(script)
    }
    script.textContent = JSON.stringify(buildPurchaseProduct(liveProduct))
  }

  function parseLiveProduct(container) {
    const jsonEl = container.querySelector('[id^="ProductJson-"]')
    if (!jsonEl?.textContent) return null
    try {
      return JSON.parse(jsonEl.textContent)
    } catch {
      return null
    }
  }

  function updatePriceEl(el, cents, isCompare) {
    if (!el) return
    const formatted = formatMoney(cents)
    el.textContent = formatted
    if (isCompare) {
      const show = cents > 0
      el.classList.toggle('sr-only', !show)
      el.toggleAttribute('hidden', !show)
      if (show) el.classList.remove('sr-only')
    }
  }

  function applyGiftCopy(row, testVariant) {
    const gift = (window.freeGifts || []).find((entry) =>
      entry.trigger_variants?.some((trigger) => Number(trigger.variant_id) === testVariant.id)
    )
    const card = row.querySelector('.flex.items-stretch.bg-white')
    const wrap = card?.closest('.mt-4')
    if (!card || !wrap) return
    if (!gift) return

    wrap.removeAttribute('hidden')
    const giftVariant = gift.gift_variant || {}
    const image = card.querySelector('img')
    if (image && giftVariant.image) {
      image.src = giftVariant.image
      image.alt = giftVariant.title || image.alt
    }
    const compare = card.querySelector('s')
    if (compare && giftVariant.compare_at_price) {
      compare.textContent = formatMoney(giftVariant.compare_at_price)
    }
    const paragraphs = card.querySelectorAll('p')
    if (gift.gift_headline && paragraphs[0]) paragraphs[0].textContent = gift.gift_headline
    if (gift.gift_description && paragraphs[1]) paragraphs[1].textContent = gift.gift_description
  }

  function rewriteVariantSelect(container, selectedId) {
    const select = container.querySelector('#variant-selector, [data-variant-select]')
    if (!select) return
    select.innerHTML = getTestProduct().variants.map(
      (variant) =>
        `<option value="${variant.id}"${Number(selectedId) === variant.id ? ' selected' : ''}>${variant.title}</option>`
    ).join('')
    select.value = String(selectedId)
  }

  function applyFrequencyPicker(container) {
    const wrapper = container.querySelector('.product__variants-wrapper') || container
    const radios = Array.from(container.querySelectorAll('.top-level_input'))
    let selectedId = getTestProduct().variants[0].id

    radios.forEach((radio) => {
      const testVariant = matchVariant(radio.value || radio.dataset.optionValue || radio.dataset.label)
      radio.dataset.optionId = String(testVariant.id)
      if (radio.checked) selectedId = testVariant.id

      const row = radio.closest('.product__form-radio-container')
      if (!row) return
      updatePriceEl(row.querySelector('[data-freq-type="compare"]'), testVariant.compareAtPrice, true)
      updatePriceEl(row.querySelector('[data-freq-type="price"]'), testVariant.price, false)
      applyGiftCopy(row, testVariant)
    })

    wrapper.dataset.currentVariantId = String(selectedId)
    container.querySelectorAll('[data-current-variant-id]').forEach((el) => {
      el.dataset.currentVariantId = String(selectedId)
    })
    return selectedId
  }

  function applyQuickView(container) {
    const selectedRadio =
      container.querySelector('.top-level_input:checked') ||
      container.querySelector('[name^="options"]:checked') ||
      container.querySelector('select[name^="options"]')
    const selectedValue = selectedRadio?.value || selectedRadio?.dataset?.optionValue || '1-month supply'
    return matchVariant(selectedValue).id
  }

  function applyForm(container, selectedId) {
    const form = container.querySelector('[data-product-form]')
    if (!form) return
    form.dataset.productHandle = getTestProduct().handle
    form.dataset.currentProductId = String(selectedId)
    form.dataset.itemId = String(selectedId)
    const idInput = form.querySelector('[name="id"]')
    if (idInput) idInput.value = String(selectedId)
    rewriteVariantSelect(form, selectedId)
  }

  function clearPending(container) {
    container?.removeAttribute('data-hair-gwp-pending')
    container?.querySelectorAll('[data-hair-gwp-pending]').forEach((el) => el.removeAttribute('data-hair-gwp-pending'))
  }

  function applyToContainer(container) {
    if (!container) return
    const liveProduct = parseLiveProduct(container)
    injectPurchaseJson(container, liveProduct)
    container.dataset.hairGwpActive = 'true'
    container.dataset.productHandle = container.dataset.productHandle || productHandleFrom(container)

    const hasFrequencyPicker = container.querySelector('.top-level_input')
    const selectedId = hasFrequencyPicker ? applyFrequencyPicker(container) : applyQuickView(container)
    applyForm(container, selectedId)
    container.querySelectorAll('[data-current-variant-id]').forEach((el) => {
      el.dataset.currentVariantId = String(selectedId)
    })
    clearPending(container)
  }

  async function maybeApply(container) {
    const handle = productHandleFrom(container)
    const isHair = isLiveHairHandle(handle) || isLiveHairHandle(pathProductHandle())
    if (!isHair) {
      clearPending(container)
      return false
    }

    try {
      const active = await isActive()
      if (!active) {
        clearPending(container)
        return false
      }
      if (!getTestProduct()) {
        console.warn('[hair-gwp-test] "Hair test purchase product" theme setting is empty; showing control')
        clearPending(container)
        return false
      }
      applyToContainer(container)
      return true
    } catch (error) {
      console.warn('[hair-gwp-test] Failed to apply test', error)
      clearPending(container)
      return false
    }
  }

  function injectCartfulScript({ quizId, flowId }, target, variant) {
    const id = `${flowId}scr-${target}`
    if (document.getElementById(id)) return
    const script = document.createElement('script')
    script.id = id
    script.src = 'https://studio-assets.cartfulsolutions.com/embed/studio.js'
    script.setAttribute('quizId', quizId)
    script.setAttribute('flowId', flowId)
    script.setAttribute('target', target)
    script.setAttribute('data-cartful-variant', variant)
    script.defer = true
    document.body.appendChild(script)
  }

  function teardownControlPageEmbed() {
    const key = `${CARTFUL.control.quizId}:${CARTFUL.control.flowId}`
    const loader = window.__cartfulQuizLoaders?.[key]
    if (loader) {
      loader.stopQuizContainerMonitoring?.()
      loader.wasManuallyClosedByUser = true
      loader.urlChangeObserver?.disconnect?.()
      loader.elementWatcher?.disconnect?.()
      if (loader.periodicCheckInterval) clearInterval(loader.periodicCheckInterval)
    }
    document.querySelectorAll('.hair-quiz-embed').forEach((el) => el.replaceChildren())
  }

  function replaceCartfulWithTest() {
    const controlScripts = Array.from(document.querySelectorAll('[data-cartful-variant="control"]'))
    const targets = controlScripts.map((script) => script.getAttribute('target')).filter(Boolean)
    controlScripts.forEach((script) => script.remove())

    targets.forEach((target) => injectCartfulScript(CARTFUL.test, target, 'test'))
  }

  async function bootCartful() {
    const active = await isActive()
    if (active) {
      teardownControlPageEmbed()
      replaceCartfulWithTest()
    }

    if (!document.querySelector('.hair-quiz-embed')) return
    const ids = active ? CARTFUL.test : CARTFUL.control
    const variant = active ? 'test' : 'control'
    if (document.querySelector(`[data-cartful-variant="${variant}"][target="page"]`)) return
    injectCartfulScript(ids, 'page', variant)
  }

  window.OneskinHairGwpTest = {
    HYPOTHESIS_ID,
    isActive,
    maybeApply,
    bootCartful,
    buildPurchaseProduct,
  }

  bootCartful()
})()
